import { createContext, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { ImagePlus, Link2Off, PenLine, Play } from "lucide-react";
import { cn } from "@/utils/helpers";
import type { CalendarPlatform, CalendarPlanSlot, CalendarSlotIdea, IdeaReference } from "@/types/contentCalendar.type";
import PlatformGlyph from "../../components/PlatformGlyph";
import { evidenceKindLabel, platformName, referenceGone } from "./ideaCardModel";

/**
 * Calendar / Idea slide (Figma 08, Library): one idea as one 16:9 slide. The
 * title in the display voice, the meta tags, the concept, the hooks as ruled
 * rows, the Objective / Sources cards (only fields the concept has; 13 Oct) or
 * Why it works when the cards are off, and the foot, with the reference (or
 * the opening frame) on the right.
 *
 * Drawn on a 1600 x 900 canvas and scaled to its box, exactly as the Figma
 * scales one component for the slide (1160 wide), the strip's thumbnails (196)
 * and the phone. Every measure below is the canvas's.
 */

export const SLIDE_W = 1600;
export const SLIDE_H = 900;

export interface IdeaSlideReference {
  /** The chip: "Instagram trend", "Saved reference", "Team media", "Link". */
  source: string;
  platform: string | null;
  still: string | null;
  video: string | null;
  /** Line one: "Old Cairo Heritage Walking Tours · 9:16 reel". */
  caption: string;
  /** Line two: "Planned 16:9 · needs adapting". */
  fit: string | null;
  /** The reference's frame fills the 9:16 box only when it is 9:16 itself. */
  tall: boolean;
}

export interface IdeaSlideData {
  title: string;
  pillar: { name: string; hue: string | null } | null;
  kind: "Video" | "Static";
  ratio: string;
  length: string | null;
  concept: string;
  hooks: string[];
  /** The concept's objective claim: the Objective card (Facts=On). Only real concept fields show (Figma 08, 13 Oct). */
  objective: string | null;
  /** The concept's sources, each with its label and link: the Sources card. */
  sources: Array<{ kind: string; label: string; url: string | null }>;
  /** The same claim as "Why it works" when the fact cards are off (the strip's thumbnails). */
  why: string | null;
  channels: CalendarPlatform[];
  where: string;
  page: string | null;
  reference: IdeaSlideReference | null;
  /** Why there is no reference: never had one, or the original is gone. */
  noReference: { gone: boolean; platform: string | null } | null;
  /** The first hook, drawn on the opening frame when there is no reference. */
  openingHook: string | null;
}

/** "75 s", "2 min 30 s", "2 min". */
export function lengthLabel(seconds: number | null | undefined): string | null {
  if (!seconds || seconds <= 0) return null;
  if (seconds < 120) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
}

function referenceSource(reference: IdeaReference): string {
  const kind = reference.kind ?? "trend";
  if (kind === "trend") {
    const platform = platformName(reference.platform);
    return platform ? `${platform} trend` : "Trend";
  }
  if (kind === "reference") return "Saved reference";
  if (kind === "media") return "Team media";
  return platformName(reference.platform) ?? "Link";
}

function frameWord(reference: IdeaReference): string {
  if (reference.mediaType === "image" || reference.imageUrl) return "picture";
  const platform = reference.platform.toLowerCase();
  if (platform === "instagram") return "reel";
  return "video";
}

/** The slide's words for one idea of one post, from real fields only. */
export function ideaSlideData(
  slot: Pick<CalendarPlanSlot, "mediaType" | "scheduledAt" | "pillarName">,
  idea: Pick<CalendarSlotIdea, "title" | "synopsis" | "hooks" | "objectiveClaim" | "sources" | "channels" | "aspectRatio" | "durationSeconds" | "reference">,
  context: {
    brandName: string;
    pillarHue: string | null;
    index: number | null;
    total: number;
  },
): IdeaSlideData {
  const day = new Date(slot.scheduledAt);
  const month = day.toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
  const dayLabel = day
    .toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" })
    .replace(",", "")
    .replace("Sept", "Sep");
  const reference = idea.reference;
  const gone = !!reference && referenceGone(reference);
  const hooks = idea.hooks.map((hook) => hook.trim()).filter(Boolean);
  return {
    title: idea.title,
    pillar: slot.pillarName ? { name: slot.pillarName, hue: context.pillarHue } : null,
    kind: slot.mediaType === "STATIC" ? "Static" : "Video",
    ratio: idea.aspectRatio,
    length: slot.mediaType === "STATIC" ? null : lengthLabel(idea.durationSeconds),
    concept: idea.synopsis,
    hooks,
    objective: idea.objectiveClaim?.trim() || null,
    sources: (idea.sources ?? []).map((source) => ({
      kind: evidenceKindLabel(source.type),
      label: source.label,
      url: source.url?.trim() || null,
    })),
    why: idea.objectiveClaim?.trim() || null,
    channels: idea.channels,
    where: [context.brandName, month, dayLabel, slot.pillarName].filter(Boolean).join(" · "),
    page: context.index !== null && context.total > 0 ? `Idea ${context.index + 1} of ${context.total}` : null,
    reference:
      reference && !gone
        ? {
            source: referenceSource(reference),
            platform: reference.platform,
            still: reference.imageUrl ?? reference.thumbnailUrl ?? null,
            video: reference.videoUrl,
            caption: `${reference.label} · ${reference.type} ${frameWord(reference)}`,
            fit: `Planned ${idea.aspectRatio}${reference.type !== idea.aspectRatio ? " · needs adapting" : ""}`,
            tall: reference.type === "9:16",
          }
        : null,
    noReference: !reference || gone ? { gone, platform: reference ? platformName(reference.platform) : null } : null,
    openingHook: hooks[0] ?? null,
  };
}

/**
 * What a picked concept's slide needs beyond the concept itself: the brand and
 * the post's day, pillar and format. The post page provides it; a
 * ConceptDocument under it draws the slide.
 */
export interface IdeaSlideContextValue {
  brandName: string;
  scheduledAt: string;
  pillarName: string | null;
  pillarHue: string | null;
}

export const IdeaSlideContext = createContext<IdeaSlideContextValue | null>(null);

/** The slide's words for a concept as a document carries it (a sent version, or the live idea). */
export function slideFromConcept(
  concept: {
    title: string;
    synopsis: string | null;
    hooks: string[];
    objectiveClaim: string | null;
    durationSeconds: number | null;
    channels: string[];
    reference: IdeaReference | null;
    aspectRatio: string | null;
    sources?: Array<{ type?: string; label: string; url?: string | null }>;
    still?: boolean;
  },
  context: IdeaSlideContextValue,
): IdeaSlideData {
  return ideaSlideData(
    { mediaType: concept.still ? "STATIC" : "VIDEO", scheduledAt: context.scheduledAt, pillarName: context.pillarName ?? "" },
    {
      title: concept.title,
      synopsis: concept.synopsis ?? "",
      hooks: concept.hooks,
      objectiveClaim: concept.objectiveClaim ?? "",
      sources: (concept.sources ?? []).map((source) => ({
        type: (source.type ?? "web") as CalendarSlotIdea["sources"][number]["type"],
        label: source.label,
        ...(source.url ? { url: source.url } : {}),
      })),
      channels: concept.channels as CalendarPlatform[],
      aspectRatio: (concept.aspectRatio ?? "9:16") as CalendarSlotIdea["aspectRatio"],
      durationSeconds: concept.durationSeconds,
      reference: concept.reference,
    },
    { brandName: context.brandName, pillarHue: context.pillarHue, index: null, total: 0 },
  );
}

/** The canvas's scale for a box: its width over 1600. */
function useCanvasScale(fixedWidth?: number) {
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(fixedWidth ? fixedWidth / SLIDE_W : 0);
  useLayoutEffect(() => {
    if (fixedWidth) return;
    const node = box.current;
    if (!node) return;
    const measure = () => setScale(node.getBoundingClientRect().width / SLIDE_W);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [fixedWidth]);
  return { box, scale };
}

/** A Tag on the canvas: Default, Pillar (with its mark) or Kind (accent outline). */
function SlideTag({ children, tone = "default", hue }: { children: ReactNode; tone?: "default" | "pillar" | "kind"; hue?: string | null }) {
  const kind = tone === "kind";
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center whitespace-nowrap rounded-[7.5px] font-mono",
        kind ? "h-[25px] px-[7.5px] text-[12.5px] tracking-[1.13px] text-co-accent-ink" : "h-[30px] gap-[10px] bg-co-tonal px-[10px] text-[13.75px] tracking-[1.65px]",
        tone === "default" && "text-co-muted",
        tone === "pillar" && "text-co-text2",
      )}
      style={{
        boxShadow: kind
          ? "inset 0 0 0 1.25px var(--co-accent)"
          : tone === "pillar" && hue
            ? `inset 0 0 0 1.25px ${hue}`
            : "inset 0 0 0 1.25px var(--co-line)",
      }}
    >
      {tone === "pillar" && hue ? <span aria-hidden="true" className="h-[15px] w-[2.75px] shrink-0" style={{ background: hue }} /> : null}
      {children}
    </span>
  );
}

/** Calendar / Idea fact card: the coloured chip, then its words. */
/**
 * Calendar / Idea fact card (library, 13 Oct) at the slide's 1600 canvas: r15,
 * p15, chip 30 tall r7.5, 17.5/25 text, gap 10. The cards hug their text; the
 * row is not stretched to a height.
 */
function FactCard({ label, tint, className, children }: { label: string; tint: string; className?: string; children: ReactNode }) {
  return (
    <div className={cn("flex min-w-0 flex-1 flex-col gap-[10px] rounded-[15px] bg-co-tonal p-[15px]", className)}>
      <span
        className="inline-flex h-[30px] w-fit shrink-0 items-center rounded-[7.5px] px-[10px] font-mono text-[13.75px] tracking-[1.65px] text-[#fafafa]"
        style={{ background: tint }}
      >
        {label}
      </span>
      <div className="relative flex min-h-0 flex-1 flex-col text-[17.5px] leading-[25px] text-co-text2">{children}</div>
    </div>
  );
}

/** "No sources linked to this idea": the recessed line in the Sources card (library Kind=Sources · 0). */
function NoSources() {
  return (
    <span className="flex items-center gap-[10px] rounded-[15px] bg-co-chrome-solid px-[12.5px] py-[11px] text-[15.6px] leading-[22.5px] text-co-text2">
      <Link2Off className="size-[17.5px] shrink-0" strokeWidth={1.6} aria-hidden="true" />
      No sources linked to this idea
    </span>
  );
}

const SECTION_LABEL = "font-section text-[16.25px] font-semibold uppercase leading-[19.3px] tracking-[1.3px] text-co-muted";

function SlideReference({
  data,
  onChangeMedia,
}: {
  data: IdeaSlideData;
  onChangeMedia?: () => void;
}) {
  const [playing, setPlaying] = useState(false);
  const reference = data.reference;
  if (!reference) {
    const gone = data.noReference?.gone;
    return (
      <div className="flex w-[450px] shrink-0 flex-col gap-[15px]">
        <div className="relative flex h-[254px] flex-col justify-between rounded-[17.5px] bg-co-tonal p-[19px]">
          <span className="font-section text-[13.75px] font-semibold uppercase leading-[16.5px] tracking-[1.1px] text-co-muted">
            Opening frame
          </span>
          {data.openingHook ? (
            <p className="line-clamp-4 font-section text-[30px] font-bold leading-[31.7px] text-co-text2">{data.openingHook}</p>
          ) : null}
        </div>
        <p className="text-[15.6px] leading-[20.7px] text-co-muted">
          {gone
            ? `This clip is no longer on ${data.noReference?.platform ?? "its platform"}. The idea still stands. Pick another reference, or keep it without one.`
            : "No reference."}
        </p>
        {onChangeMedia ? (
          <button
            type="button"
            onClick={onChangeMedia}
            className="inline-flex h-10 w-fit items-center gap-[10px] rounded-full px-[17.5px] text-[16.25px] font-medium text-co-accent-ink outline-none hover:bg-co-tonal focus-visible:outline-[3px] focus-visible:outline-co-accent"
          >
            Add media
            <PenLine className="size-[18px]" strokeWidth={1.8} aria-hidden="true" />
          </button>
        ) : null}
      </div>
    );
  }
  return (
    <div className="group/ref relative h-[800px] w-[450px] shrink-0 overflow-hidden rounded-[17.5px] bg-co-tonal">
      {playing && reference.video ? (
        <video src={reference.video} controls autoPlay playsInline className="size-full object-contain" />
      ) : reference.still ? (
        // The media fills the card whatever its shape; playing it shows the whole frame.
        <img src={reference.still} alt="" className="size-full object-cover" />
      ) : reference.video ? (
        <video src={reference.video} muted playsInline preload="metadata" className="size-full object-cover" />
      ) : null}
      {!playing ? (
        <>
          <span className="absolute left-[15px] top-[15px] inline-flex h-[33px] items-center gap-[7.5px] rounded-[7.5px] bg-[#121212]/40 px-[11.25px] text-[15px] font-medium text-[#fafafa] shadow-[inset_0_0_0_1.25px_rgba(255,255,255,0.12)] backdrop-blur">
            {reference.platform ? <PlatformGlyph platform={reference.platform as CalendarPlatform} className="size-[16.5px] text-[#a3a3a3]" /> : null}
            {reference.source}
          </span>
          <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-[175px] bg-gradient-to-t from-black/70 to-transparent" />
          <p className="absolute bottom-[18px] left-[16.5px] right-[16.5px] text-[15.6px] leading-[22px] text-white">
            {reference.caption}
            {reference.fit ? (
              <>
                <br />
                {reference.fit}
              </>
            ) : null}
          </p>
          {reference.video ? (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              aria-label="Play the reference"
              className="absolute inset-0 grid place-items-center opacity-0 transition-opacity group-hover/ref:opacity-100 focus-visible:opacity-100"
            >
              <span className="grid size-[76px] place-items-center rounded-full bg-black/50 text-white">
                <Play className="size-[28px] translate-x-[2px]" fill="currentColor" strokeWidth={0} />
              </span>
            </button>
          ) : null}
          {onChangeMedia ? (
            <button
              type="button"
              onClick={onChangeMedia}
              className="absolute right-[15px] top-[15px] inline-flex h-[33px] items-center gap-[7.5px] rounded-[7.5px] bg-[#121212]/60 px-[11.25px] text-[15px] font-medium text-[#fafafa] opacity-0 backdrop-blur transition-opacity group-hover/ref:opacity-100 focus-visible:opacity-100"
            >
              <ImagePlus className="size-[16.5px]" strokeWidth={1.8} aria-hidden="true" />
              Change media
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * The slide itself. `width` fixes it (the strip's 196px thumbnails); without
 * it the slide fills its box. `facts` draws the Insight / Objective / Evidence
 * row (Facts=On); the strip draws the slide without it, as the library does.
 */
export default function IdeaSlide({
  data,
  width,
  facts = true,
  onChangeMedia,
  className,
  overlay,
  ariaLabel,
}: {
  data: IdeaSlideData;
  width?: number;
  facts?: boolean;
  /** Team only: change the reference ("Change media", or "Add media" with none). */
  onChangeMedia?: () => void;
  className?: string;
  /** Drawn over the slide in page pixels (the rewriting card). */
  overlay?: ReactNode;
  ariaLabel?: string;
}) {
  const { box, scale } = useCanvasScale(width);
  const style: CSSProperties = width ? { width, height: (width * SLIDE_H) / SLIDE_W } : { aspectRatio: `${SLIDE_W} / ${SLIDE_H}` };
  return (
    <div ref={box} role="group" aria-label={ariaLabel ?? data.title} className={cn("relative w-full overflow-hidden bg-co-surface text-left", className)} style={style}>
      <div
        className="absolute left-0 top-0 flex origin-top-left gap-[60px] px-[80px] py-[40px] text-co-text2"
        style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${scale})`, visibility: scale ? "visible" : "hidden" }}
        aria-hidden={width ? true : undefined}
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <h2 className="line-clamp-3 font-display text-[57.5px] font-bold leading-[55.2px] tracking-[-0.58px] text-co-text2">{data.title}</h2>
          <div className="mt-[12px] flex flex-wrap items-center gap-[10px]">
            {data.pillar ? (
              <SlideTag tone="pillar" hue={data.pillar.hue}>
                {data.pillar.name}
              </SlideTag>
            ) : null}
            <SlideTag tone="kind">{data.kind}</SlideTag>
            <SlideTag>{data.ratio}</SlideTag>
            {data.length ? <SlideTag>{data.length}</SlideTag> : null}
          </div>
          <p className="mt-[12.4px] line-clamp-3 text-[21.25px] leading-[30.3px] text-co-text2">{data.concept}</p>
          {data.hooks.length ? (
            <>
              <p className={cn(SECTION_LABEL, "mt-[12.4px]")}>Hooks</p>
              <ol className="mt-[2.75px] flex flex-col">
                {data.hooks.slice(0, 3).map((hook, index) => (
                  <li
                    key={index}
                    className="flex gap-[17.5px] py-[3.75px]"
                    style={index > 0 ? { boxShadow: "inset 0 1.25px 0 var(--co-line)" } : undefined}
                  >
                    <span className="w-[37px] shrink-0 pt-[4px] font-section text-[23.75px] font-bold leading-[26.2px] text-co-accent-ink">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="line-clamp-2 min-w-0 flex-1 pt-[1px] text-[20px] font-medium leading-[27.6px] text-co-text2">“{hook}”</span>
                  </li>
                ))}
              </ol>
            </>
          ) : null}
          {facts ? (
            // Facts=On (library, 13 Oct): only fields the concept has. The Objective
            // card carries its claim and the Sources card its links; no Insight card
            // until the concept gets an insight field, and no second "Why it works".
            // The Objective card sets the row's height (up to five lines); the Sources
            // card stretches to it and scrolls past what fits, every source two lines.
            <div className="mt-[12.4px] flex items-stretch gap-[15px]">
              {data.objective ? (
                <FactCard label="Objective" tint="var(--co-pillar-3)">
                  <span data-comment-part="objective" className="line-clamp-5">
                    {data.objective}
                  </span>
                </FactCard>
              ) : null}
              <FactCard label={`Sources · ${data.sources.length}`} tint="var(--co-pillar-2)">
                {data.sources.length ? (
                  <span
                    data-comment-part="sources"
                    className={cn("flex flex-col gap-[10px] overflow-y-auto", data.objective ? "absolute inset-0" : "max-h-[170px]")}
                  >
                    {data.sources.map((item, index) => (
                      <span key={index} className="line-clamp-2 shrink-0">
                        <span className="text-co-muted">{item.kind} · </span>
                        {item.url ? (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noreferrer"
                            className="pointer-events-auto underline-offset-2 hover:underline"
                          >
                            {item.label}
                          </a>
                        ) : (
                          item.label
                        )}
                      </span>
                    ))}
                  </span>
                ) : (
                  <NoSources />
                )}
              </FactCard>
            </div>
          ) : data.why ? (
            // Facts=Off: the claim reads as "Why it works" under the hooks.
            <>
              <p className={cn(SECTION_LABEL, "mt-[9.7px]")}>Why it works</p>
              <p className="mt-[2.75px] line-clamp-2 text-[17.5px] leading-[24.8px] text-co-text2">{data.why}</p>
            </>
          ) : null}
          <div className="mt-auto flex items-center gap-[20px] pt-[11px]">
            <span className="flex shrink-0 gap-[7.5px]">
              {data.channels.map((channel) => (
                <span
                  key={channel}
                  className="grid size-[33px] place-items-center rounded-[7.5px] bg-[#121212]/40 shadow-[inset_0_0_0_1.25px_rgba(255,255,255,0.08)]"
                >
                  <PlatformGlyph platform={channel} className="size-[18px] text-[#fafafa]" />
                </span>
              ))}
            </span>
            <span className="min-w-0 flex-1 truncate text-[15px] leading-[20.7px] text-co-muted">{data.where}</span>
            {data.page ? (
              <span className="shrink-0 font-section text-[18.75px] font-semibold leading-[22px] text-co-muted">{data.page}</span>
            ) : null}
          </div>
        </div>
        <SlideReference data={data} onChangeMedia={width ? undefined : onChangeMedia} />
      </div>
      {overlay}
    </div>
  );
}
